import { axios, APIBASE_REVIVERAPPLICATION } from "./ApiConfig";
import {
  Condition,
  Type,
  BoxType,
  CompanyStatistics,
  CreateOrderFromContract,
  FullLoadPrices,
  Order,
  OrderStatus,
  CompanyOrders,
  GroupOrder,
  OrderAmount,
  CreditOrderCheckRequest,
  CreditOrderCheckResponse,
  Statistics,
  MyOrderList,
  RegisterOrderDeviation,
  OrderDeviation,
  CreateOrderActiveValues,
  AmountLeftInLoopAnswer,
  CreateOrder,
  Filters,
  PagedOrders,
  AmountsForOrder,
  OrderFilter,
  ISODateTimeString,
  OrderTimeSlot,
  OrderSchedule,
} from "@/types";
import { QueryClient, useQueries, useQuery, UseQueryOptions } from "@tanstack/react-query";
import qs from "qs";

interface OrderForCompanyParams {
  companyId: number;
  includeInactive?: boolean;
  languageId?: number;
  pageNumber?: number | null;
  excludeContractOrders?: boolean | null;
  limit?: number | null;
  sortBy?: string | null;
  sortDirection?: string | null;
  statuses?: OrderStatus[] | null;
  orderTypes?: number[] | null;
  onlyPromoted?: boolean | null;
  awaitingProcessingOnly?: boolean | null;
  salesItemId?: number | null;
}

export const getOrdersForCompany = (params: OrderForCompanyParams) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/getOrdersForCompany`, { params });

export const useGetOrdersForCompany = (
  params: OrderForCompanyParams,
  options?: Partial<UseQueryOptions<CompanyOrders>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    enabled: params.companyId !== undefined,
    ...options,
  };

  return useQuery<CompanyOrders>({
    queryKey: [
      "getOrdersForCompany",
      params.companyId,
      params.includeInactive,
      params.pageNumber,
      params.limit,
      params.sortBy,
      params.sortDirection,
      params.statuses,
      params.orderTypes,
      params.onlyPromoted,
      params.awaitingProcessingOnly,
      params.salesItemId,
      params.excludeContractOrders,
    ],
    queryFn: async () => {
      const result = await getOrdersForCompany(params);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getMyOrderList = (
  companyId: any,
  includeInactive: any,
  pageNumber: any,
  excludeContractOrders: boolean = false,
  languageId?: number
) =>
  axios.get<MyOrderList>(`${APIBASE_REVIVERAPPLICATION}api/orders/getMyOrderList`, {
    params: {
      companyId,
      includeInactive,
      pageNumber,
      excludeContractOrders,
      languageId,
    },
  });

export const useGetMyOrderList = (
  companyId: number,
  includeInactive: boolean = true,
  pageNumber: number = 1,
  excludeContractOrders: boolean = false,
  languageId?: number,
  options?: Partial<UseQueryOptions<MyOrderList>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<MyOrderList>({
    queryKey: ["getMyOrderList", companyId, pageNumber, languageId],
    queryFn: async () => {
      const result = await getMyOrderList(companyId, includeInactive, pageNumber, excludeContractOrders, languageId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getOrder = (orderId: number, companyId?: number, languageId?: number) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/${orderId}`, {
    params: {
      companyId,
      languageId,
    },
  });

export const useGetOrderById = (
  orderId?: number | null,
  companyId?: number,
  languageId?: number,
  options?: Partial<UseQueryOptions<Order>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Order>({
    queryKey: ["getOrderById", orderId, companyId, languageId],
    queryFn: async () => {
      const result = await getOrder(orderId as number, companyId, languageId);
      return result.data;
    },
    enabled: orderId !== undefined,
    ...queryOptions,
  });
};

export const prefetchOrderById = (queryClient: QueryClient, orderId: number, companyId?: number, languageId?: number) =>
  queryClient.prefetchQuery({
    queryKey: ["getOrderById", orderId, companyId, languageId],
    queryFn: async () => (await getOrder(orderId, companyId, languageId)).data as Order,
    staleTime: 30_000,
  });

export const prefetchMyOrderList = (
  queryClient: QueryClient,
  companyId: number,
  includeInactive: boolean,
  pageNumber: number,
  excludeContractOrders: boolean = false,
  languageId?: number
) =>
  queryClient.prefetchQuery({
    queryKey: ["getMyOrderList", companyId, pageNumber, languageId],
    queryFn: async () =>
      (await getMyOrderList(companyId, includeInactive, pageNumber, excludeContractOrders, languageId)).data,
    staleTime: 30_000,
  });

export const useGetOrdersByIds = (orderIds: number[], options?: any) => {
  const defaultOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  const orderQueries = useQueries<Order[]>({
    queries: orderIds.map((orderId) => ({
      queryKey: ["getOrderById", orderId],
      queryFn: async () => {
        const result = await getOrder(orderId);
        return result.data;
      },
      enabled: orderIds.length > 0 && options?.enabled !== false,
      ...defaultOptions,
    })),
  });

  const isLoading = orderQueries.some((query) => query.isLoading);
  const isError = orderQueries.some((query) => query.isError);
  const errors = orderQueries.filter((query) => query.isError).map((query) => query.error);

  const orders = orderQueries.filter((query) => query.isSuccess).map((query) => query.data) as Order[];

  return {
    orders,
    isLoading,
    isError,
    errors,
    queries: orderQueries,
  };
};

export const getOrderByTransactionId = (transactionId: any, companyId: any) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/getOrderByTransactionAndCompanyId/${transactionId}/${companyId}`);

export const useGetOrderByTransactionId = (transactionId: any, companyId: any, options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Order | undefined>({
    queryKey: ["getOrderByTransactionId", transactionId, companyId],
    queryFn: async () => {
      const result = await getOrderByTransactionId(transactionId, companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const postFilteredOrders = async (filter: OrderFilter) =>
  axios.post<PagedOrders>(`${APIBASE_REVIVERAPPLICATION}api/orders/filteredOrders`, filter);

export const getFilters = async (currency: string, unit: string) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/filters?currency=${currency}&unit=${unit}`);

export const useGetFilters = (currency: string, unit: string, options?: Partial<UseQueryOptions<Filters>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Filters>({
    queryKey: ["getFilters", currency, unit],
    queryFn: async () => {
      const result = await getFilters(currency, unit);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getCreateOrderFromOrder = async (orderId: any, companyId: any, userId: any, priceToPay: any) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/createOrderFromOrder`, {
    params: {
      orderId,
      companyId,
      userId,
      priceToPay,
    },
  });

export const useGetCreateOrderFromOrder = (
  orderId: number,
  companyId: number,
  userId: number,
  priceToPay: number,
  options?: Partial<UseQueryOptions<CreateOrder>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<CreateOrder>({
    queryKey: ["getCreateOrderFromOrder", orderId, companyId, userId, priceToPay],
    queryFn: async () => {
      const result = await getCreateOrderFromOrder(orderId, companyId, userId, priceToPay);
      return result.data;
    },
    ...queryOptions,
  });
};

export const postCreateOrder = async (order: CreateOrder | any) =>
  axios.post(`${APIBASE_REVIVERAPPLICATION}api/orders/create`, order);

export const postCreateOrderFromContract = async (orderData: CreateOrderFromContract) =>
  axios.post(`${APIBASE_REVIVERAPPLICATION}api/orders/createOrderFromContract`, orderData);

export const deleteOrder = async (orderId: any) =>
  axios.delete(`${APIBASE_REVIVERAPPLICATION}api/orders/${orderId}/delete`);

export const getCreateOrderValues = async () => axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/create/values`);

export const useGetCreateOrderValues = (options?: Partial<UseQueryOptions<CreateOrderActiveValues>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<CreateOrderActiveValues>({
    queryKey: ["getCreateOrderValues"],
    queryFn: async () => {
      const result = await getCreateOrderValues();
      return result.data;
    },
    ...queryOptions,
  });
};

export const postReleaseReservation = async (orderId: any) =>
  axios.post(`${APIBASE_REVIVERAPPLICATION}api/orders/releaseReservation/${orderId}`);

export const getValidConditions = async (salesItemId: any) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/create/validConditions/${salesItemId}`);

export const useGetValidConditions = (salesItemId: number | null, options?: Partial<UseQueryOptions<Condition[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Condition[]>({
    queryKey: ["getValidConditions", salesItemId],
    queryFn: async () => {
      const result = await getValidConditions(salesItemId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getValidTypes = async (salesItemId: any, conditionId: any) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/create/validConditions/${salesItemId}/validTypes/${conditionId}`);

export const useGetValidTypes = (
  salesItemId: number,
  conditionId: number,
  options?: Partial<UseQueryOptions<Type[]>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Type[]>({
    queryKey: ["getValidTypes", salesItemId],
    queryFn: async () => {
      const result = await getValidTypes(salesItemId, conditionId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getOrderAmounts = async (orderId: any) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/${orderId}/amounts`);

export const useGetOrderAmounts = (orderId: number, options?: Partial<UseQueryOptions<AmountsForOrder>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<AmountsForOrder>({
    queryKey: ["getOrderAmounts", orderId],
    queryFn: async () => {
      const result = await getOrderAmounts(orderId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getStatistics = async () => axios.get(`${APIBASE_REVIVERAPPLICATION}api/admin/orderStatistics`);

export const useGetStatistics = (options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Statistics | undefined>({
    queryKey: ["getStatistics"],
    queryFn: async () => {
      const result = await getStatistics();
      return result.data;
    },
    ...queryOptions,
  });
};

export const postInvoice = async (orderId: any, taskId: any) => {
  const taskConfig = {
    headers: {
      TaskId: taskId,
    },
  };
  await axios.post(`${APIBASE_REVIVERAPPLICATION}api/orders/invoice/${orderId}`, "", taskConfig);
};

export const postHandleTransport = async (orderId: any, transportCompaniesIds: any, taskId: any) => {
  const taskConfig = {
    headers: {
      TaskId: taskId,
    },
  };

  await axios.post(
    `${APIBASE_REVIVERAPPLICATION}api/orders/handleTransport`,
    {
      orderId,
      transportCompaniesIds,
    },
    taskConfig
  );
};

export const postUpdateOrder = async (orderId: number, order: CreateOrder) =>
  await axios.post(`${APIBASE_REVIVERAPPLICATION}api/orders/${orderId}/update`, order);

export const postPromoteOrder = async (orderId: any) =>
  await axios.post(`${APIBASE_REVIVERAPPLICATION}api/orders/promote/${orderId}`);

export const getStaleTransactions = async () =>
  await axios.get(`${APIBASE_REVIVERAPPLICATION}api/admin/staleTransactions`);

export const getBoxtypeValues = async () => axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/create/values/boxtypes`);

export const useGetBoxtypeValues = (options?: Partial<UseQueryOptions<BoxType[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<BoxType[]>({
    queryKey: ["getBoxtypeValues"],
    queryFn: async () => {
      const result = await getBoxtypeValues();
      return result.data.boxtypes;
    },
    ...queryOptions,
  });
};

export const postCalculateTransportPrice = async (
  orderId: any,
  postcode: any,
  country: any,
  place: any,
  currentCompanyId: any,
  amount: any,
  numberOfBoxes: any,
  boxtypeId: any
) =>
  await axios.post(`${APIBASE_REVIVERAPPLICATION}api/orders/calculateTransportPrice`, {
    orderId,
    postcode,
    country,
    place,
    currentCompanyId,
    amount,
    numberOfBoxes,
    boxtypeId,
  });

export const postCalculateTransportPrices = async (
  orderId: any,
  address: any,
  postCode: any,
  place: any,
  countryCodeIso3: any,
  countryCodeIso2: any,
  isEu: any
) =>
  await axios.post(`${APIBASE_REVIVERAPPLICATION}api/orders/calculateTransportPrices`, {
    orderId,
    address: {
      address,
      postCode,
      place,
      countryCodeIso3,
      countryCodeIso2,
      isEu,
    },
  });

export const getSalesItemById = async (salesItemId: any) =>
  await axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/salesitem/${salesItemId}`);

export const postFinalAmounts = async (orderAmounts: OrderAmount[], taskId: number) => {
  const taskConfig = {
    headers: {
      TaskId: taskId,
    },
  };
  return await axios.post(`${APIBASE_REVIVERAPPLICATION}api/orders/setFinalAmounts`, orderAmounts, taskConfig);
};

export const postFinalAmount = async (orderId: number, amount: number, taskId: number) => {
  const taskConfig = {
    headers: {
      TaskId: taskId,
    },
  };
  return await axios.post(
    `${APIBASE_REVIVERAPPLICATION}api/orders/${orderId}/setFinalAmount/${amount}`,
    "",
    taskConfig
  );
};

const getStatisticsForCompanies = async (from: Date, to: Date, companyIds: number[], salesItemIds: number[]) =>
  await axios.get(`${APIBASE_REVIVERAPPLICATION}api/statistics/company`, {
    params: {
      from,
      to,
      companyIds,
      salesItemIds,
    },
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: "repeat" });
    },
  });

export const useGetStatisticsForCompanies = (
  from: Date,
  to: Date,
  companyIds: number[],
  salesItemIds: number[],
  options?: any
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<CompanyStatistics[]>({
    queryKey: ["getStatisticsForCompanies", from, to, companyIds, salesItemIds],
    queryFn: async () => {
      const result = await getStatisticsForCompanies(from, to, companyIds, salesItemIds);
      return result.data;
    },
    ...queryOptions,
  });
};

const getFullLoadPriceOffers = async (transactionId: number) => {
  return axios.get(`${APIBASE_REVIVERAPPLICATION}api/transport/fullLoadPriceOffers/${transactionId}`);
};

export const useGetFullLoadPriceOffers = (transactionId: number, options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<FullLoadPrices | undefined>({
    queryKey: ["getFullLoadPriceOffers", transactionId],
    queryFn: async () => {
      const result = await getFullLoadPriceOffers(transactionId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const postConfirmTransportCompanyChoice = async (transactionId: number, companyId: number | null) => {
  return axios.post(`${APIBASE_REVIVERAPPLICATION}api/transport/confirmTransportCompanyChoice`, {
    transactionId,
    companyId,
  });
};

export const postGroupOrders = async (group: GroupOrder) => {
  return axios.post(`${APIBASE_REVIVERAPPLICATION}api/orders/group`, group);
};

export const getCollectiveOrderIds = async (orderId: number) => {
  return axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/group/${orderId}`);
};

export const useGetCollectiveOrderIds = (orderId: number, options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<number[] | undefined>({
    queryKey: ["getGroupOrderIds", orderId],
    queryFn: async () => {
      const result = await getCollectiveOrderIds(orderId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const postCheckCreditOrder = async (data: CreditOrderCheckRequest) =>
  axios.post<CreditOrderCheckResponse>(`${APIBASE_REVIVERAPPLICATION}api/credit/checkOrder`, data);

export const postRegisterDeviation = async (orderId: number, data: RegisterOrderDeviation) =>
  axios.post<RegisterOrderDeviation>(`${APIBASE_REVIVERAPPLICATION}api/orders/${orderId}/deviation`, data);

const getDeviationsByOrderId = async (orderId: number) =>
  axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/${orderId}/deviations`);

export const useGetDeviationsByOrderId = (orderId: number, options?: Partial<UseQueryOptions<OrderDeviation[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<OrderDeviation[]>({
    queryKey: ["getDeviationsByOrderId", orderId],
    queryFn: async () => {
      const result = await getDeviationsByOrderId(orderId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const prefetchDeviationsByOrderId = (queryClient: QueryClient, orderId: number) =>
  queryClient.prefetchQuery({
    queryKey: ["getDeviationsByOrderId", orderId],
    queryFn: async () => (await getDeviationsByOrderId(orderId)).data as OrderDeviation[],
    staleTime: 30_000,
  });

const getClosedLoopRemainingAmount = async (companySpecificProductId: number) =>
  axios.get<AmountLeftInLoopAnswer>(
    `${APIBASE_REVIVERAPPLICATION}api/orders/amountLeftForProduct/${companySpecificProductId}`
  );

export const useGetClosedLoopRemainingAmount = (
  companySpecificProductId: number,
  options?: Partial<UseQueryOptions<AmountLeftInLoopAnswer>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<AmountLeftInLoopAnswer>({
    queryKey: ["getClosedLoopRemainingAmount", companySpecificProductId],
    queryFn: async () => {
      const result = await getClosedLoopRemainingAmount(companySpecificProductId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const attachDocumentsFromArhiveToOrder = async (orderId: number, documentIds: string[]) => {
  return axios.post(`${APIBASE_REVIVERAPPLICATION}api/orders/${orderId}/attachments/addFromArchive`, {
    documentIds: documentIds,
  });
};

export const getOrderAttachments = async (orderId: number) => {
  return await axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/${orderId}/attachments`);
};

export const useGetOrderAttachments = (orderId: number, options?: Partial<UseQueryOptions<string[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<string[]>({
    queryKey: ["orderAttachments", orderId],
    queryFn: async () => {
      const result = await getOrderAttachments(orderId);
      if (!result || !result.data) {
        return [];
      }
      return result.data.documentIds;
    },
    ...queryOptions,
  });
};
interface ProcessOrdersParams {
  orderId: number;
  taskId?: number;
  startDate?: Date;
  endDate?: Date;
}

export const postProcessOrder = async (params: ProcessOrdersParams) => {
  const taskConfig = {
    headers: {
      TaskId: params.taskId,
    },
  };

  const date = {
    startDate: params.startDate,
    endDate: params.endDate,
  };

  return axios.post(`${APIBASE_REVIVERAPPLICATION}api/orders/${params.orderId}/process`, date, taskConfig);
};

const getOrderTimeSlots = async (
  companyId: number,
  startDate?: ISODateTimeString | Date,
  endDate?: ISODateTimeString | Date
) => {
  const now = new Date();

  // Default start: one year ago (same month/day as today, one year earlier)
  const defaultStart = new Date(now);
  defaultStart.setFullYear(defaultStart.getFullYear() - 1);

  // Default end: end of current year (Dec 31 23:59:59.999)
  const defaultEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);

  const start = startDate ? new Date(startDate) : defaultStart;
  const end = endDate ? new Date(endDate) : defaultEnd;

  return axios.get(`${APIBASE_REVIVERAPPLICATION}api/orders/schedule/${companyId}`, {
    params: {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    },
  });
};

export const useGetOrderTimeSlots = (
  companyId: number,
  startDate?: ISODateTimeString | Date,
  endDate?: ISODateTimeString | Date,
  options?: Partial<UseQueryOptions<OrderSchedule>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<OrderSchedule>({
    queryKey: ["orderTimeSlots", companyId, startDate, endDate],
    queryFn: async () => {
      const result = await getOrderTimeSlots(companyId, startDate, endDate);
      return result.data;
    },
    ...queryOptions,
  });
};
